import * as THREE from 'three';
import { BLACK, basic, cloth, emis, std } from '../materials.ts';
import { M, Ribbon, V, glowSprite, limb, wisp } from '../prims.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * El Dragón Ahogado (La Orilla 5, docs/SAGAS.md): the real dragon's bones, the one that burned the titan centuries ago,
 * raised off the sea floor by a nightmare that got inside them. Not the Turtle's dragon of smoke and glass: this is a
 * skeleton, black burned bone worn pale on its ridges, crusted with red and pink coral and sea-green sponge, kelp
 * hanging off the ribs and the stumps of its wings. A long neck of vertebrae, a horned skull with the jaw hung open, a
 * barrel of ribs open to the sky with fire burning inside it where the heart was (or, with the heart gone to the
 * forge, an empty chest with steam rising out of it: `steam`). Faces +Z.
 *
 * Bones (anim/creatures/sunken.ts): `body` pivots at the hips and carries the chest, the fore legs, the wings and the
 * neck; `neck` at the shoulders carries `head`, which carries `jaw`; `tail` and `tail2` trail behind the hips; the hind
 * legs hang from the model group. Each leg bends at its knee.
 */

/** Hip, knee and foot of each leg (the right side, +X; the left mirrors x): front, then rear. */
const FORE = { hip: V(0.62, 1.72, 0.95), knee: V(0.78, 0.92, 1.25), foot: V(0.74, 0.1, 1.05) };
const HIND = { hip: V(0.55, 1.62, -1.05), knee: V(0.72, 0.95, -0.7), foot: V(0.7, 0.1, -1.15) };
/** Where the chest's fire burns (model units, before scaling): the layer's glow and the breath come from here. */
export const SUNKEN_CHEST = V(0, 1.75, 0.15);
/** Its mouth (at rest), where the breath leaves it. */
export const SUNKEN_MOUTH = V(0, 2.75, 3.15);

export const sunken = (o?: BaseOpts & { steam?: boolean }): Model => {
  const c = begin(o, 1.6, 171);
  const { g, R, rng } = c;
  const steam = !!o?.steam;
  const bone = std('#4a433e', { roughness: 0.55, flatShading: true, bumpScale: 0.06 });
  const boneD = std('#2b2523', { roughness: 0.62, flatShading: true, bumpScale: 0.07 });
  // Worn pale where the sea scoured it: the ridges, the teeth, the claws read from far off.
  const pale = std('#b3aa9c', { roughness: 0.5, flatShading: true });
  const wet = std('#1c2226', { roughness: 0.08, metalness: 0.35 });
  const coralR = std('#b4483c', { roughness: 0.8, flatShading: true });
  const coralP = std('#d7877a', { roughness: 0.8, flatShading: true });
  const sponge = std('#3c7570', { roughness: 0.9, flatShading: true });
  const kelp = cloth('#283f26', { side: THREE.DoubleSide, flatShading: true });
  const fire = steam ? emis('#c8dce6', '#9fc4d8', 0.6) : emis('#ffd2a0', '#ff6a2a', 2.4);
  const embers = steam ? emis('#5a6a72', '#3a4a52', 0.3) : emis('#ff8a6e', '#ff4f3a', 1.4);

  const bodyJ = joint(g, V(0, 1.62, -1.05), 'body');
  const bd = bodyJ.abs;
  const neckJ = joint(bd, V(0, 2.0, 1.25), 'neck');
  const nk = neckJ.abs;
  const headJ = joint(nk, V(0, 2.95, 2.45), 'head');
  const hd = headJ.abs;
  const jawJ = joint(hd, V(0, 2.82, 2.6), 'jaw');
  const jw = jawJ.abs;
  const tailJ = joint(bd, V(0, 1.6, -1.55), 'tail');
  const tl = tailJ.abs;
  const tail2J = joint(tl, V(0, 1.0, -3.0), 'tail2');
  const tl2 = tail2J.abs;
  const foreJ = [-1, 1].map((k) => joint(bd, V(k * FORE.hip.x, FORE.hip.y, FORE.hip.z), k < 0 ? 'foreL' : 'foreR'));
  const fore2J = [-1, 1].map((k, i) => joint(foreJ[i]!.abs, V(k * FORE.knee.x, FORE.knee.y, FORE.knee.z), k < 0 ? 'foreL2' : 'foreR2'));
  const hindJ = [-1, 1].map((k) => joint(g, V(k * HIND.hip.x, HIND.hip.y, HIND.hip.z), k < 0 ? 'hindL' : 'hindR'));
  const hind2J = [-1, 1].map((k, i) => joint(hindJ[i]!.abs, V(k * HIND.knee.x, HIND.knee.y, HIND.knee.z), k < 0 ? 'hindL2' : 'hindR2'));
  const wingJ = [-1, 1].map((k) => joint(bd, V(k * 0.55, 2.35, 0.75), k < 0 ? 'wingL' : 'wingR'));

  const sphere = new THREE.SphereGeometry(0.5, 9, 6);
  const knob = (parent: THREE.Object3D, mat: THREE.Material, r: number, p: THREE.Vector3, sx = 1, sy = 1, sz = 1): THREE.Mesh => {
    const m = M(sphere, mat, parent);
    m.position.copy(p);
    m.scale.set(r * 2 * sx, r * 2 * sy, r * 2 * sz);
    return m;
  };
  const coralGeo = new THREE.ConeGeometry(0.05, 0.22, 4);
  /** A tuft of coral: a few branches fanning up off a bone, red or pink, a sponge lump at the foot now and then. */
  const coral = (parent: THREE.Object3D, p: THREE.Vector3, s = 1): void => {
    const mat = rng() < 0.55 ? coralR : coralP;
    const n = 3 + Math.floor(rng() * 3);
    for (let i = 0; i < n; i++) {
      const b = M(coralGeo, mat, parent);
      b.position.set(p.x + R() * 0.1 * s, p.y + 0.06 * s, p.z + R() * 0.1 * s);
      b.rotation.set(R() * 1.1, R() * 3, R() * 1.1);
      b.scale.setScalar(s * (0.7 + 0.6 * rng()));
    }
    if (rng() < 0.45) knob(parent, sponge, 0.07 * s, p, 1.2, 0.7, 1);
  };
  /** Kelp hanging off a bone: a strip falling to a ragged point. */
  const weed = (parent: THREE.Object3D, top: THREE.Vector3, len: number): void => {
    const gm = new THREE.PlaneGeometry(0.12, len, 1, 4);
    gm.translate(0, -len / 2, 0);
    const p = gm.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < p.count; i++) {
      const d = -p.getY(i) / len;
      p.setX(i, p.getX(i) * (1 - 0.8 * d) + 0.06 * Math.sin(d * 5 + i));
      p.setZ(i, 0.05 * Math.sin(d * 4));
    }
    gm.computeVertexNormals();
    const m = M(gm, kelp, parent);
    m.position.copy(top);
    m.rotation.y = R() * 3;
  };
  const spike = new THREE.ConeGeometry(0.07, 0.34, 4);

  // ---- The spine: a row of vertebrae from the hips to the shoulders, a ridge of pale spines along it.
  for (let i = 0; i <= 8; i++) {
    const t = i / 8;
    const p = V(0, 1.72 + 0.48 * Math.sin(t * Math.PI * 0.85) + 0.15 * t, -1.1 + 2.35 * t);
    knob(bd, i % 2 ? bone : boneD, 0.11, p, 1.1, 0.8, 0.9);
    const s = M(spike, pale, bd);
    s.position.set(0, p.y + 0.22, p.z - 0.04);
    s.rotation.x = -0.35;
    s.scale.setScalar(0.8 + 0.6 * Math.sin(t * Math.PI));
    if (i === 2 || i === 6) coral(bd, V(0.12, p.y + 0.12, p.z), 0.9);
  }
  // The cord running through the vertebrae, so the spine reads as one line of bone.
  M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(Array.from({ length: 9 }, (_, i) => V(0, 1.72 + 0.48 * Math.sin((i / 8) * Math.PI * 0.85) + 0.15 * (i / 8), -1.1 + 2.35 * (i / 8)))), 12, 0.06, 5), boneD, bd);
  // The pelvis: two heavy blades over the hips.
  for (const k of [-1, 1]) {
    const p = knob(bd, bone, 0.3, V(k * 0.32, 1.72, -1.05), 1.2, 0.55, 1.4);
    p.rotation.z = k * 0.4;
  }

  // ---- The ribs: an open barrel of curved bones round the chest, black, their tips worn pale, the fire inside.
  const ribG = new THREE.TorusGeometry(0.62, 0.045, 4, 12, Math.PI * 0.95);
  for (let i = 0; i < 7; i++) {
    const z = -0.6 + i * 0.27;
    const r = 0.85 + 0.25 * Math.sin(((i + 0.5) / 7) * Math.PI);
    for (const k of [-1, 1]) {
      const rib = M(ribG, i % 2 ? bone : boneD, bd);
      rib.position.set(0, 1.95 + 0.1 * Math.sin((i / 6) * Math.PI), z);
      rib.rotation.set(0, Math.PI / 2, k > 0 ? Math.PI / 2 + 0.25 : Math.PI / 2 - 0.25);
      rib.scale.set(r, r * 1.05, 1);
      if (k > 0) rib.rotation.y = -Math.PI / 2;
      // The broken ones end short.
      if (rng() < 0.2) rib.scale.multiplyScalar(0.75);
    }
    if (i % 2 === 0) coral(bd, V((i % 4 ? 1 : -1) * (0.75 + 0.1 * R()), 1.6 + 0.2 * R(), z), 1.1);
    if (i % 3 === 1) weed(bd, V((i % 2 ? -1 : 1) * 0.82, 1.55, z), 0.6 + 0.3 * rng());
  }
  // The breastbone under them, a keel of bone.
  limb(bd, V(0, 1.05, -0.3), V(0, 1.18, 1.0), 0.09, 0.12, boneD, 6);
  // The fire inside the ribs: a core of flame, embers crusting the inner bones (steam: a cold empty glow).
  knob(bd, fire, steam ? 0.16 : 0.3, SUNKEN_CHEST, 1, 0.85, 1.1);
  for (let i = 0; i < 5; i++) knob(bd, embers, 0.07 + 0.04 * rng(), V(R() * 0.7, 1.35 + R() * 0.4, SUNKEN_CHEST.z + R() * 0.9), 1.4, 0.6, 1);
  const chestGlow = glowSprite(bd, steam ? '#cfe6f2' : '#ff6a2a', steam ? 2.0 : 2.8, SUNKEN_CHEST.clone(), steam ? 0.25 : 0.5, true);
  const coreGlow = glowSprite(bd, steam ? '#ffffff' : '#ffd2a0', steam ? 0.8 : 1.3, SUNKEN_CHEST.clone(), steam ? 0.35 : 0.75, true);

  // ---- The neck: vertebrae rising forward from the shoulders, spines on top, coral on the turns.
  const neckPts = [V(0, 2.05, 1.25), V(0, 2.35, 1.65), V(0, 2.7, 2.0), V(0, 2.92, 2.35)];
  const neckCurve = new THREE.CatmullRomCurve3(neckPts);
  for (let i = 0; i < 6; i++) {
    const p = neckCurve.getPointAt(i / 5);
    knob(nk, i % 2 ? bone : boneD, 0.105 - i * 0.006, p, 1, 0.9, 1.1);
    const s = M(spike, pale, nk);
    s.position.set(0, p.y + 0.18, p.z - 0.05);
    s.rotation.x = -0.5;
    s.scale.setScalar(0.7);
  }
  M(new THREE.TubeGeometry(neckCurve, 8, 0.055, 5), boneD, nk);
  coral(nk, V(-0.1, 2.5, 1.8), 0.8);
  weed(nk, V(0.1, 2.35, 1.6), 0.7);

  // ---- The skull: long and wedge-shaped, deep eye sockets with a fire in them, a crown of swept-back horns.
  knob(hd, bone, 0.32, V(0, 3.02, 2.55), 1.1, 0.85, 1.25);
  knob(hd, boneD, 0.22, V(0, 2.95, 2.95), 0.95, 0.7, 1.5);
  // The snout, flat and long, ending in a hooked tip.
  limb(hd, V(0, 2.95, 2.9), V(0, 2.88, 3.35), 0.13, 0.07, bone, 6);
  const hook = M(new THREE.ConeGeometry(0.07, 0.2, 5), pale, hd);
  hook.position.set(0, 2.82, 3.4);
  hook.rotation.x = Math.PI * 0.75;
  for (const k of [-1, 1]) {
    // The eye sockets: dark holes with a coal in each.
    knob(hd, BLACK, 0.09, V(k * 0.2, 3.1, 2.75), 1, 0.8, 1);
    knob(hd, embers, 0.04, V(k * 0.21, 3.1, 2.79));
    glowSprite(hd, steam ? '#bfe0ee' : '#ff6a2a', 0.35, V(k * 0.22, 3.1, 2.83), 0.6);
    // The horns: two long ones swept back over the neck, a short pair under them.
    const horn = new THREE.CatmullRomCurve3([V(k * 0.18, 3.2, 2.45), V(k * 0.32, 3.38, 2.15), V(k * 0.4, 3.45, 1.75), V(k * 0.42, 3.4, 1.4)]);
    M(new THREE.TubeGeometry(horn, 8, 0.05, 5), pale, hd);
    const tip = M(new THREE.ConeGeometry(0.05, 0.18, 5), pale, hd);
    tip.position.set(k * 0.42, 3.38, 1.32);
    tip.rotation.x = -Math.PI / 2 - 0.1;
    const small = M(new THREE.ConeGeometry(0.05, 0.3, 5), bone, hd);
    small.position.set(k * 0.3, 2.95, 2.4);
    small.rotation.set(-1.9, 0, k * 0.4);
  }
  coral(hd, V(0.15, 3.25, 2.6), 0.6);
  // The upper teeth, long and uneven.
  const fang = new THREE.ConeGeometry(0.03, 0.17, 4);
  for (let i = 0; i < 8; i++) {
    for (const k of [-1, 1]) {
      const t = M(fang, pale, hd);
      t.position.set(k * (0.12 - i * 0.005), 2.8, 2.85 + i * 0.065);
      t.rotation.x = Math.PI;
      t.scale.y = 0.7 + 0.5 * Math.abs(Math.sin(i * 1.9 + k));
    }
  }
  // ---- The jaw: a long lower jaw hanging open, teeth up, kelp caught on it.
  for (const k of [-1, 1]) limb(jw, V(k * 0.16, 2.78, 2.6), V(k * 0.07, 2.62, 3.3), 0.06, 0.04, boneD, 5);
  knob(jw, boneD, 0.06, V(0, 2.62, 3.32), 1.4, 0.8, 1);
  for (let i = 0; i < 6; i++) {
    for (const k of [-1, 1]) {
      const t = M(fang, pale, jw);
      t.position.set(k * (0.13 - i * 0.013), 2.78 - i * 0.025, 2.75 + i * 0.1);
      t.scale.y = 0.6 + 0.4 * Math.abs(Math.cos(i * 1.3 + k));
    }
  }
  weed(jw, V(0.08, 2.62, 3.1), 0.45);
  // A light inside the mouth while it draws breath (animated by the clips scaling the jaw open; it is always faintly there).
  const mouthGlow = glowSprite(hd, steam ? '#e8f4ff' : '#ff8a3a', 0.9, V(0, 2.8, 2.95), 0.3, true);

  // ---- The tail: vertebrae trailing back and down, thinning to a bony tip, spines on top.
  const tailCurve1 = new THREE.CatmullRomCurve3([V(0, 1.62, -1.5), V(0, 1.4, -2.2), V(0, 1.05, -2.95)]);
  for (let i = 0; i < 6; i++) {
    const p = tailCurve1.getPointAt(i / 5);
    knob(tl, i % 2 ? bone : boneD, 0.1 - i * 0.008, p, 1, 0.85, 1.1);
    const s = M(spike, pale, tl);
    s.position.set(0, p.y + 0.15, p.z);
    s.rotation.x = -0.6;
    s.scale.setScalar(0.55);
  }
  M(new THREE.TubeGeometry(tailCurve1, 8, 0.05, 5), boneD, tl);
  coral(tl, V(0, 1.4, -2.2), 0.8);
  const tailCurve2 = new THREE.CatmullRomCurve3([V(0, 1.0, -3.05), V(0, 0.6, -3.7), V(0.1, 0.3, -4.3)]);
  for (let i = 0; i < 6; i++) {
    const p = tailCurve2.getPointAt(i / 5);
    knob(tl2, i % 2 ? bone : boneD, 0.065 - i * 0.007, p, 1, 0.85, 1.1);
  }
  M(new THREE.TubeGeometry(tailCurve2, 8, 0.035, 5), boneD, tl2);
  const tip = M(new THREE.ConeGeometry(0.08, 0.4, 5), pale, tl2);
  tip.position.set(0.12, 0.25, -4.5);
  tip.rotation.x = -Math.PI / 2 - 0.4;
  weed(tl2, V(0, 0.75, -3.5), 0.4);

  // ---- The legs: heavy bones with knobbed joints, long pale claws.
  const claw = new THREE.ConeGeometry(0.045, 0.28, 4);
  for (const [k, i] of [[-1, 0], [1, 1]] as const) {
    for (const [D, up, lo, fore] of [[FORE, foreJ[i]!.abs, fore2J[i]!.abs, true], [HIND, hindJ[i]!.abs, hind2J[i]!.abs, false]] as const) {
      const hip = V(k * D.hip.x, D.hip.y, D.hip.z), knee = V(k * D.knee.x, D.knee.y, D.knee.z), foot = V(k * D.foot.x, D.foot.y, D.foot.z);
      limb(up, hip, knee, fore ? 0.11 : 0.13, 0.09, bone, 6);
      knob(up, boneD, fore ? 0.13 : 0.15, hip, 1.1, 1, 1.1);
      knob(lo, boneD, 0.09, knee);
      limb(lo, knee, foot.clone().add(V(0, 0.08, 0)), 0.08, 0.06, bone, 6);
      knob(lo, bone, 0.1, foot.clone().add(V(0, 0.06, 0)), 1.4, 0.6, 1.4);
      for (const a of [-0.45, 0, 0.45]) {
        const cl = M(claw, pale, lo);
        cl.position.set(foot.x + Math.sin(a) * 0.14, 0.07, foot.z + Math.cos(a) * 0.14 + 0.06);
        cl.rotation.set(Math.PI / 2 - 0.3, 0, -a);
      }
      if (fore) coral(up, hip.clone().lerp(knee, 0.45).add(V(k * 0.08, 0, 0)), 0.7);
      else weed(up, hip.clone().lerp(knee, 0.3), 0.55);
    }
  }

  // ---- The wings: the arm bones of each wing, broken off, a fan of finger bones with rags of kelp for membrane.
  for (const [k, i] of [[-1, 0], [1, 1]] as const) {
    const w = wingJ[i]!.abs;
    const sh = V(k * 0.55, 2.35, 0.75);
    const el = V(k * 1.45, 3.15, 0.35);
    const wr = V(k * 2.2, 2.85, -0.35);
    limb(w, sh, el, 0.09, 0.07, bone, 6);
    knob(w, boneD, 0.11, el);
    limb(w, el, wr, 0.065, 0.05, bone, 6);
    knob(w, boneD, 0.08, wr);
    // The fingers: long, thin, some snapped; a ragged sheet of kelp hung between two of them.
    const fingers = [V(k * 2.9, 2.2, -1.1), V(k * 2.5, 1.7, -1.4), V(k * 1.9, 1.75, -1.25)];
    fingers.forEach((f, j) => {
      const end = j === 1 ? wr.clone().lerp(f, 0.6) : f;
      limb(w, wr, end, 0.04, 0.02, j === 1 ? boneD : bone, 5);
    });
    const sheet = new THREE.BufferGeometry();
    const a = wr, b = fingers[0]!, cc = fingers[2]!;
    const pts: number[] = [];
    const N = 5;
    for (let s2 = 0; s2 < N; s2++) {
      const t0 = s2 / N, t1 = (s2 + 1) / N;
      const p0 = a.clone().lerp(b, 0.15 + t0 * 0.7), p1 = a.clone().lerp(b, 0.15 + t1 * 0.7);
      const q0 = a.clone().lerp(cc, 0.15 + t0 * 0.6), q1 = a.clone().lerp(cc, 0.15 + t1 * 0.6);
      const sag = -0.25 - 0.2 * rng();
      const m0 = p0.clone().lerp(q0, 0.5).add(V(0, sag, 0)), m1 = p1.clone().lerp(q1, 0.5).add(V(0, sag, 0));
      // Torn: some strips missing.
      if (rng() < 0.25) continue;
      pts.push(...p0.toArray(), ...m0.toArray(), ...p1.toArray(), ...p1.toArray(), ...m0.toArray(), ...m1.toArray());
      pts.push(...m0.toArray(), ...q0.toArray(), ...m1.toArray(), ...m1.toArray(), ...q0.toArray(), ...q1.toArray());
    }
    sheet.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
    sheet.computeVertexNormals();
    M(sheet, kelp, w);
    coral(w, el.clone().add(V(0, 0.08, 0)), 0.8);
  }

  // A wet sheen on the shoulders and the skull's crown, where the sea still runs off it.
  knob(bd, wet, 0.25, V(0, 2.2, 0.9), 1.6, 0.25, 1);
  knob(hd, wet, 0.18, V(0, 3.2, 2.6), 1, 0.2, 1.2);

  // Smoke (fire) or steam (no heart) rising out of the ribs, and off the skull.
  const ribs: Ribbon[] = [];
  const wm = steam ? basic('#e8f2f6', 0.35) : basic('#3a2a2a', 0.4);
  const SMOKE: [number, number, number, number][] = [[0.0, 2.3, 0.15, 1.1], [0.25, 2.2, -0.4, 0.8], [-0.2, 3.3, 2.6, 0.6]];
  for (const [x, y, z, h] of SMOKE) ribs.push(new Ribbon(z > 2 ? hd : bd, wisp(x, z, h, 6).map((p) => p.add(V(0, y, 0))), steam ? 0.07 : 0.05, wm, rng, 0.08));

  // Only the boil and the fire's flicker here: every move is the clips'.
  const t = (n: number): void => {
    for (const r of ribs) r.step();
    const f = ((n * 5) % 7) / 7;
    chestGlow.material.opacity = (steam ? 0.22 : 0.42) + 0.12 * f;
    coreGlow.material.opacity = (steam ? 0.3 : 0.65) + 0.15 * (1 - f);
    mouthGlow.material.opacity = 0.22 + 0.08 * f;
  };
  t(0);
  return done(c, 3.5, t, undefined, bonesOf(c, {
    body: bodyJ.p, neck: neckJ.p, head: headJ.p, jaw: jawJ.p, tail: tailJ.p, tail2: tail2J.p,
    foreL: foreJ[0]!.p, foreL2: fore2J[0]!.p, foreR: foreJ[1]!.p, foreR2: fore2J[1]!.p,
    hindL: hindJ[0]!.p, hindL2: hind2J[0]!.p, hindR: hindJ[1]!.p, hindR2: hind2J[1]!.p,
    wingL: wingJ[0]!.p, wingR: wingJ[1]!.p,
  }));
};
